import { useState, useEffect, useMemo } from 'react';
import { Box, Container, Paper, Typography, useTheme, Button, Fab, useScrollTrigger, Zoom } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import SEO from '../components/SEO';

// Back to top button component
const ScrollTop = ({ children }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
      >
        {children}
      </Box>
    </Zoom>
  );
};

const Privacy = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const [activeSection, setActiveSection] = useState('');

  // Table of contents wrapped in useMemo
  const sections = useMemo(() => [
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'information-usage', title: 'How We Use Information' },
    { id: 'cookies', title: 'Cookies and Analytics' },
    { id: 'data-sharing', title: 'Sharing Data' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'user-rights', title: 'Your Rights (GDPR / CCPA)' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'contact', title: 'Contact' }
  ], []);

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Adjust based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <SEO 
        title="Privacy Policy | Gravity Point Media" 
        description="Privacy policy for Gravity Point Media's website support and maintenance services." 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 4, sm: 6 }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Table of Contents */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                height: 'fit-content',
                position: { md: 'sticky' },
                top: { md: 100 }, // Adjust based on header height
                width: { md: 280 },
                display: { xs: 'none', md: 'block' }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                Table of Contents
              </Typography>
              <Box component="nav">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      color: activeSection === section.id ? theme.palette.primary.main : 'text.secondary',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      mb: 1,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {section.title}
                  </Button>
                ))}
              </Box>
            </Paper>

            {/* Main Content */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 6 },
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                flex: 1
              }}
            >
              <Box textAlign="center" mb={6}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                  }}
                >
                  Privacy Policy
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Effective Date: {new Date().toLocaleDateString()}
                </Typography>
              </Box>

              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                This Privacy Policy describes how Gravity Point Media LLC collects, uses, and protects your data through GPM Support.
              </Typography>

              {/* Information Collection Section */}
              <Box id="information-collection" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  1. Information We Collect
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We collect:
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Name, email, business information
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Billing details
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Website credentials (only when needed for support)
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Usage data (via Google Analytics)
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Communication logs (emails, tickets)
                  </Typography>
                </Box>
              </Box>

              {/* Information Usage Section */}
              <Box id="information-usage" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  2. How We Use Information
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We use your data to:
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Deliver support and development services
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Manage billing and transactions
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Communicate updates and resolve tickets
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Improve our website and services
                  </Typography>
                </Box>
              </Box>

              {/* Cookies Section */}
              <Box id="cookies" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  3. Cookies and Analytics
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We use cookies and Google Analytics to improve user experience and monitor performance. By using our site, you consent to the use of cookies.
                </Typography>
              </Box>

              {/* Data Sharing Section */}
              <Box id="data-sharing" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  4. Sharing Data
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We do not sell your data. We only share it with:
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Amazon SES (for email delivery)
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Our internal support team strictly for service fulfillment
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Payment processor (Polar.sh / Stripe) to complete transactions
                  </Typography>
                </Box>
              </Box>

              {/* Data Retention Section */}
              <Box id="data-retention" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  5. Data Retention
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We retain personal data, support tickets, and logs indefinitely for support history, audits, legal compliance, and quality assurance. Clients may request data access or deletion at any time, subject to legal retention obligations.
                </Typography>
              </Box>

              {/* User Rights Section */}
              <Box id="user-rights" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  6. Your Rights (GDPR / CCPA)
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  If you are a resident of the US, Canada, or an applicable jurisdiction:
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    You may request access, correction, or deletion of your personal data.
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    You may opt out of non-essential data processing.
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Submit requests via support@gravitypointmedia.com.
                  </Typography>
                </Box>
              </Box>

              {/* Data Security Section */}
              <Box id="data-security" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  7. Data Security
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We implement reasonable technical and organizational safeguards to protect your data from unauthorized access, misuse, or disclosure. However, no digital platform can guarantee 100% security.
                </Typography>
              </Box>

              {/* Contact Section */}
              <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  8. Contact
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  For privacy or policy-related questions, contact:
                  <br />
                  support@gravitypointmedia.com
                </Typography>
              </Box>

              <Box mt={6} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  © {currentYear} Gravity Point Media LLC. All rights reserved.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Back to Top Button */}
      <ScrollTop>
        <Fab color="primary" size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </>
  );
};

export default Privacy; 